import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Button, Alert, Spinner } from 'react-bootstrap';
import { MapPin, Clock, Users, Truck, DollarSign, Package, AlertCircle } from 'react-feather';
import axios from 'axios';

const statusButtons = [
  { label: 'Just In', value: 'Just In', variant: 'outline-primary' },
  { label: 'Waiting Payment', value: 'Waiting Payment', variant: 'outline-primary' },
  { label: 'New Order', value: 'New Order', variant: 'outline-primary' },
  { label: 'In Transit', value: 'In Transit', variant: 'outline-primary' },
  { label: 'Trip Ended', value: 'Trip Ended', variant: 'outline-primary' },
  { label: 'Waiting Driver', value: 'Waiting Driver', variant: 'outline-primary' },
];

const TripStatus = () => {
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchTrip();
  }, []);

  const fetchTrip = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const res = await axios.get('https://srv547457.hstgr.cloud:3011/trip/3');
      setTrip(res.data[0]);
    } catch (err) {
      setError('Failed to load trip details.');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    if (!trip) return;
    setUpdating(true);
    setError('');
    setSuccess('');
    try {
      await axios.put('https://srv547457.hstgr.cloud:3011/trip/3', {
        ...trip,
        status: newStatus,
      });
      setTrip({ ...trip, status: newStatus });
      setSuccess('Status updated successfully');
      setTimeout(() => setSuccess(''), 2000);
    } catch (err) {
      setError('Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const DetailRow = ({ label, value, icon: Icon }) => (
    <div className="d-flex align-items-center py-2 border-bottom">
      {Icon && <Icon size={16} className="text-primary me-2" />}
      <div className="text-muted" style={{ width: '140px' }}>{label}</div>
      <div className="flex-grow-1 fw-medium">{value || '-'}</div>
    </div>
  );

  return (
    <div style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      <div className="container-fluid px-4 py-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div className="d-flex align-items-center gap-3">
            <h4 className="mb-0">Trip Management</h4>
            <span className="badge bg-white text-primary border border-primary fs-6">#{trip?.trip_id}</span>
            <span className="badge bg-white text-primary border border-primary fs-6">{trip?.status}</span>
          </div>
          <Button variant="outline-primary" size="sm" onClick={() => navigate('/MarketplaceOrders')}>
            Back to Orders
          </Button>
        </div>

        {loading ? (
          <div className="d-flex justify-content-center py-5">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : error ? (
          <Alert variant="danger">{error}</Alert>
        ) : (
          <>
            {success && <Alert variant="success" className="mb-4">{success}</Alert>}

            <div className="row g-4">
              <div className="col-lg-8">
                <Card className="shadow-sm border-0 h-100">
                  <Card.Header className="bg-white border-bottom py-3">
                    <h5 className="mb-0">Trip Details</h5>
                  </Card.Header>
                  <Card.Body>
                    <div className="mb-4">
                      <h6 className="text-primary mb-3">Status Update</h6>
                      <div className="d-flex flex-wrap gap-2">
                        {statusButtons.map(btn => (
                          <Button
                            key={btn.value}
                            variant={trip.status === btn.value ? 'primary' : btn.variant}
                            size="sm"
                            disabled={updating || trip.status === btn.value}
                            onClick={() => handleStatusUpdate(btn.value)}
                            className="px-3"
                          >
                            {btn.label}
                            {updating && trip.status !== btn.value && (
                              <Spinner size="sm" animation="border" className="ms-2" />
                            )}
                          </Button>
                        ))}
                      </div>
                    </div>

                    <div className="row g-4">
                      <div className="col-md-6">
                        <h6 className="text-primary mb-3">Location Information</h6>
                        <DetailRow icon={MapPin} label="Origin" value={trip.origin_location} />
                        <DetailRow icon={MapPin} label="Destination" value={trip.dest_location} />
                        <DetailRow icon={MapPin} label="Distance" value={`${trip.distance} km`} />
                      </div>
                      <div className="col-md-6">
                        <h6 className="text-primary mb-3">Customer Details</h6>
                        <DetailRow icon={Users} label="Customer ID" value={trip.cust_id} />
                        <DetailRow icon={Users} label="Contact" value={trip.delivery_contact_details} />
                        <DetailRow icon={Users} label="Passengers" value={trip.number_of_passengers} />
                      </div>
                    </div>

                    <div className="row g-4 mt-2">
                      <div className="col-md-6">
                        <h6 className="text-primary mb-3">Vehicle Requirements</h6>
                        <DetailRow icon={Truck} label="Vehicle Type" value={trip.preferred_car_type} />
                        <DetailRow icon={Users} label="Driver Gender" value={trip.preferred_gender} />
                        <DetailRow icon={Users} label="Driver Age" value={trip.preferred_age} />
                      </div>
                      <div className="col-md-6">
                        <h6 className="text-primary mb-3">Payment Information</h6>
                        <DetailRow icon={DollarSign} label="Payment Type" value={trip.payment_type} />
                        <DetailRow icon={Clock} label="Payment Timing" value={trip.paying_when} />
                        <DetailRow icon={DollarSign} label="Cost" value={`${trip.currency_symbol}${trip.delivery_cost_proposed}`} />
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </div>

              <div className="col-lg-4">
                <div className="row g-4">
                  <div className="col-12">
                    <Card className="shadow-sm border-0">
                      <Card.Header className="bg-white border-bottom py-3">
                        <h5 className="mb-0">Delivery Status</h5>
                      </Card.Header>
                      <Card.Body>
                        <DetailRow icon={Package} label="Priority" value={trip.delivery_priority || 'Standard'} />
                        <DetailRow icon={AlertCircle} label="Category" value={trip.commercial_value_delivery_category} />
                        <DetailRow icon={Package} label="Confirmation" value={trip.delivery_received_confirmation_code} />
                      </Card.Body>
                    </Card>
                  </div>

                  <div className="col-12">
                    <Card className="shadow-sm border-0">
                      <Card.Header className="bg-white border-bottom py-3">
                        <h5 className="mb-0">Time Information</h5>
                      </Card.Header>
                      <Card.Body>
                        <DetailRow icon={Clock} label="Request Date" 
                          value={new Date(trip.request_start_datetime).toLocaleString()} />
                        <DetailRow icon={Clock} label="Start Date" 
                          value={new Date(trip.order_start_datetime).toLocaleString()} />
                        <DetailRow icon={Clock} label="End Date" 
                          value={trip.order_end_datetime || 'Pending'} />
                      </Card.Body>
                    </Card>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TripStatus;